import { useState } from 'react'
import { DynamicFormRenderer } from '@/modules/forms/DynamicFormRenderer'
import { useFormBuilderState } from './FormBuilderState'

/**
 * Live preview of the in-progress `formSchema`. Per design D1 + ADR-0010,
 * the preview mounts the same `DynamicFormRenderer` end-users see at
 * runtime — never a parallel preview implementation, never `@rjsf/core`.
 * What the admin sees here is what the requester will see.
 *
 * **Data sources**: the runtime renderer talks to `apiClient` directly for
 * autocomplete / data-source lookups. We do NOT mock those for the preview
 * (the design's "mocked data sources" suggestion was a guideline, not a
 * requirement). The trade-off is that authoring an autocomplete field
 * fires a real GET against `/requests/data-sources/:key/search` — but the
 * endpoint is read-only and admin-only, and seeing real values is more
 * useful than seeing fakes. If admin-stage data starts feeling unsafe to
 * browse during authoring, swap to a `dataSources` mock layer in a
 * follow-up.
 *
 * **Empty state**: when the schema has no fields, the preview shows a
 * placeholder asking the admin to add one on the Visual tab.
 *
 * **No submit handler**: the preview's submit button is disabled (we pass
 * `submitting={false}` and no `onSubmit`). Admins shouldn't accidentally
 * submit a real request from the authoring surface.
 *
 * **Re-render budget**: per NFR5-001, the preview must re-render under
 * 300 ms after a debounced edit. The state hook is local to this
 * component (only `formSchema` reads from context); React's reconciliation
 * over a typical ~10-field schema is comfortably under budget on modern
 * hardware. No memoisation is required at this scope.
 */
export function FormPreview() {
	const { formSchema } = useFormBuilderState()
	const [value, setValue] = useState<Record<string, unknown>>({})

	if (formSchema.fields.length === 0) {
		return (
			<div
				role="region"
				aria-label="Form preview"
				className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground"
			>
				<p className="font-medium">Add fields on the Visual tab to see them rendered here</p>
				<p className="mt-1">
					The preview uses the same renderer the request submitter will see — so what you build is what they get.
				</p>
			</div>
		)
	}

	return (
		<div role="region" aria-label="Form preview" className="rounded-md border bg-card p-4" data-testid="form-preview">
			{/* No `onSubmit` is passed — the renderer only mounts its submit
			    button when one is provided, so admins literally cannot submit
			    a real request from the preview tab. The disclaimer below
			    confirms it visually. */}
			<DynamicFormRenderer formSchema={formSchema} value={value} onChange={setValue} />
			<p className="mt-3 text-xs text-muted-foreground">Preview only — submitting from this tab does nothing.</p>
		</div>
	)
}
